import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { searchAdminActors, updateActorVisibility, updateActorStatus } from '../../../api/adminApi';

const AVATAR_COLORS = ['#7F77DD','#D4537E','#1D9E75','#BA7517','#185FA5','#993556','#0F6E56','#993C1D'];
const getAvatarColor = id => AVATAR_COLORS[id % AVATAR_COLORS.length];

const FILTERS = [
  { key: 'all',       label: '전체' },
  { key: 'public',    label: '공개 프로필' },
  { key: 'private',   label: '비공개 프로필' },
  { key: 'active',    label: '활성' },
  { key: 'suspended', label: '정지' },
  { key: 'male',      label: '남성' },
  { key: 'female',    label: '여성' },
];

export default function AdminTalentsPage() {
  const navigate = useNavigate();
  const [query,   setQuery]   = useState('');
  const [type,    setType]    = useState('name');
  const [filter,  setFilter]  = useState('all');
  const [actors,  setActors]  = useState([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [activeTerm, setActiveTerm] = useState('');

  const LIMIT = 20;

  // 검색어·필터·페이지 중 하나만 바뀌어도 요청이 나가므로, 늦게 도착한
  // 예전 응답이 최신 결과를 덮어쓰지 않도록 요청마다 번호를 매긴다.
  const reqIdRef = useRef(0);
  // 검색 버튼이 누르는 즉시 반영해야 하는 조건 — state 갱신을 기다리지 않는다
  const searchTermRef = useRef({ q: '', type: 'name' });

  const fetchActors = useCallback(async (q, t, f, p) => {
    const myReq = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const data = await searchAdminActors({ q, type: t, filter: f, page: p, limit: LIMIT });
      if (myReq !== reqIdRef.current) return; // 더 최신 요청이 이미 나갔다
      setActors(data.actors);
      setTotal(data.total);
    } catch (err) {
      if (myReq !== reqIdRef.current) return;
      const status = err.response?.status;
      setError(
        status
          ? `배우 목록을 불러오지 못했습니다. (HTTP ${status})`
          : (err.message || '배우 목록을 불러오지 못했습니다.')
      );
      setActors([]);
      setTotal(0);
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
  }, []);

  // 필터·페이지가 바뀌면 '확정된 검색어'로 다시 조회한다.
  // query state를 직접 쓰면 입력만 하고 검색을 안 누른 글자까지 반영돼 결과가 튄다.
  useEffect(() => {
    const { q, type: t } = searchTermRef.current;
    fetchActors(q, t, filter, page);
  }, [filter, page, fetchActors]);

  // 검색 유형(이름↔전화번호)을 바꾸면 같은 검색어로 다시 조회한다.
  // 예전에는 검색 버튼을 다시 눌러야 해서 유형만 바꾼 사용자는 결과가 안 바뀐다고 느꼈다.
  const handleTypeChange = (nextType) => {
    setType(nextType);
    if (!searchTermRef.current.q) return;
    searchTermRef.current = { ...searchTermRef.current, type: nextType };
    fetchActors(searchTermRef.current.q, nextType, filter, 1);
    setPage(1);
  };

  const handleClearSearch = () => {
    setQuery('');
    searchTermRef.current = { q: '', type };
    setActiveTerm('');
    if (page !== 1) setPage(1);
    else fetchActors('', type, filter, 1);
  };

  const handleSearch = () => {
    searchTermRef.current = { q: query.trim(), type };
    setActiveTerm(query.trim());
    if (page !== 1) {
      setPage(1);      // useEffect가 새 검색어로 조회한다 (중복 요청 방지)
    } else {
      fetchActors(query.trim(), type, filter, 1);
    }
  };

  const toggleVisibility = async (id, current) => {
    const next = !current;
    setActors(prev => prev.map(t => t.id === id ? { ...t, profilePublic: next } : t));
    try {
      await updateActorVisibility(id, next);
    } catch {
      setActors(prev => prev.map(t => t.id === id ? { ...t, profilePublic: current } : t));
    }
  };

  const toggleStatus = async (id, current) => {
    const next = current === 'active' ? 'suspended' : 'active';
    setActors(prev => prev.map(t => t.id === id ? { ...t, status: next } : t));
    try {
      await updateActorStatus(id, next);
    } catch {
      setActors(prev => prev.map(t => t.id === id ? { ...t, status: current } : t));
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <AdminLayout pageId="actors">
      <div className="admin-topbar">
        <h1>배우 검색</h1>
        <div className="admin-topbar__right">
          <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
            총 {total.toLocaleString()}명
          </span>
          <span className="admin-badge"><i className="ti ti-user" /> admin</span>
        </div>
      </div>

      {/* 검색 바 */}
      <div className="ag-card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <select
            className="ag-field__input"
            value={type}
            onChange={e => handleTypeChange(e.target.value)}
            style={{ width: 150, flexShrink: 0 }}
          >
            <option value="name">이름으로 검색</option>
            <option value="phone">전화번호로 검색</option>
          </select>
          <input
            className="ag-field__input"
            type="text"
            placeholder="배우 이름 또는 전화번호를 입력하세요"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            style={{ flex: 1 }}
          />
          <button className="ag-btn ag-btn--primary" style={{ flexShrink: 0 }} onClick={handleSearch}>
            <i className="ti ti-search" /> 검색
          </button>
        </div>
        {activeTerm && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
              &lsquo;{activeTerm}&rsquo; 검색 중
            </span>
            <button className="ag-btn ag-btn--sm" onClick={handleClearSearch}>
              <i className="ti ti-x" /> 검색 초기화
            </button>
          </div>
        )}
      </div>

      {/* 필터 칩 */}
      <div className="ag-filter-row">
        <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)', flexShrink: 0 }}>필터:</span>
        {FILTERS.map(f => (
          <button
            key={f.key}
            className={`ag-filter-chip${filter === f.key ? ' active' : ''}`}
            onClick={() => { setFilter(f.key); setPage(1); }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ fontSize: 12, color: 'var(--color-danger)', marginBottom: 10 }}>
          <i className="ti ti-alert-triangle" /> {error}
        </div>
      )}

      {/* 결과 수 */}
      <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 10 }}>
        {loading ? '불러오는 중...' : `${total.toLocaleString()}명의 배우`}
      </div>

      {/* 테이블 */}
      <div className="ag-card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="ag-table">
          <thead>
            <tr>
              {['배우', '성별', '출생연도', '연락처', '프로필', '상태', '관리'].map(h => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="ag-table__empty">
                  <i className="ti ti-loader" style={{ fontSize: 20, display: 'block', marginBottom: 8 }} />
                  불러오는 중...
                </td>
              </tr>
            ) : actors.length === 0 ? (
              <tr>
                <td colSpan={7} className="ag-table__empty">
                  <i className="ti ti-search" style={{ fontSize: 20, display: 'block', marginBottom: 8 }} />
                  검색 결과가 없습니다
                </td>
              </tr>
            ) : actors.map(t => (
              /* 행 전체를 눌러 상세로 간다 — 조회수 TOP3에서는 되는데 여기서만 안 돼
                 이름을 눌러도 아무 일이 없었다. 칸 안의 버튼은 각자 동작을 유지한다. */
              <tr
                key={t.id}
                style={{ cursor: 'pointer' }}
                title={`${t.name} 상세 보기`}
                onClick={() => navigate(`/admin/talents/${t.id}`)}
              >
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div
                      className="ag-avatar"
                      style={{ background: getAvatarColor(t.id) + '22', color: getAvatarColor(t.id) }}
                    >
                      {t.name.slice(0, 2)}
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{t.name}</span>
                    {t.isTest && (
                      <span className="ag-badge ag-badge--warn" style={{ fontSize: 10, padding: '1px 6px' }}>
                        테스트
                      </span>
                    )}
                  </div>
                </td>
                <td style={{ color: 'var(--color-text-secondary)' }}>
                  {t.gender?.toUpperCase() === 'MALE' ? '남' : '여'}
                </td>
                <td style={{ color: 'var(--color-text-secondary)' }}>{t.birthYear}년생</td>
                <td style={{ color: 'var(--color-text-secondary)', fontVariantNumeric: 'tabular-nums' }}>{t.phone}</td>
                <td>
                  <button
                    className={`ag-badge ${t.profilePublic ? 'ag-badge--success' : 'ag-badge--gray'}`}
                    style={{ border: 'none', cursor: 'pointer' }}
                    onClick={e => { e.stopPropagation(); toggleVisibility(t.id, t.profilePublic); }}
                    title="클릭하여 전환"
                  >
                    {t.profilePublic ? '공개' : '비공개'}
                  </button>
                </td>
                <td>
                  <button
                    className={`ag-badge ${t.status === 'active' ? 'ag-badge--success' : 'ag-badge--danger'}`}
                    style={{ border: 'none', cursor: 'pointer' }}
                    onClick={e => { e.stopPropagation(); toggleStatus(t.id, t.status); }}
                    title="클릭하여 전환"
                  >
                    {t.status === 'active' ? '활성' : '정지'}
                  </button>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                    <button
                      className="ag-btn ag-btn--primary ag-btn--sm"
                      onClick={() => navigate(`/admin/talents/${t.id}`)}
                    >
                      <i className="ti ti-edit" /> 수정
                    </button>
                    <button
                      className="ag-btn ag-btn--sm"
                      onClick={() => navigate(`/admin/talents/${t.id}`)}
                    >
                      <i className="ti ti-history" /> 이력
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="ag-pagination">
          <button className="ag-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
            <i className="ti ti-chevron-left" />
          </button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page + i - 3;
            if (p < 1 || p > totalPages) return null;
            return (
              <button
                key={p}
                className={`ag-page-btn${page === p ? ' active' : ''}`}
                onClick={() => setPage(p)}
              >
                {p}
              </button>
            );
          })}
          <button className="ag-page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
            <i className="ti ti-chevron-right" />
          </button>
        </div>
      )}
    </AdminLayout>
  );
}
